import React, { useContext, useEffect, useState } from "react";
import { useInfiniteQuery, useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { List, notification } from "antd";
import {
    getAddressUserTag,
    getLicenseUserTag,
    getCreditUserTag,
    getRoleUserTag,
    TagType,
} from "@Components/groups/TagProperties";
import UserGroupService from "@Services/UserGroupService";
import AddressService from "@Services/AddressService";
import { IAddressDetailsPaged } from "@Models/IAddressDetails";
import CreditService from "@Services/CreditService";
import { ICreditDetailsPaged } from "@Models/ICreditDetails";
import { ILicenseCreationDetails, ILicenseDetailsPaged } from "@Models/ILicenseDetails";
import UserService from "@Services/UserService";
import { IRoleAssignmentPaged } from "@Models/IRoleDetails";
import { IGroupEntry, INavState, ITag, ITagMinimal } from "./GroupUtils";
import { SkeletonRows } from "@Components/shared/SkeletonRows";
import InfiniteUserList from "@Components/groups/InfiniteUserList";
import LicenseService from "@Services/LicenseService";
import { GroupContext, QueryKeys, SystemContext, UserContext } from "@Constants";
import { MainContext } from "@Context/provider";
import "@Styles/pages/groupsPage.scss";
import SessionManager from "@Core/session";
import { IGroupAssignmentDetails } from "@Models/IUserGroupDetails";
import { findLowestContext, getSystemId, prettifyName } from "@Utils";
import { IUserDetailsPaged } from "@Models/IUserDetails";
import { checkCapWithContext } from "@Utils";
import { QueryKeys as GroupsQueryKeys } from "@Features/groups/constants";

type IProps = {
    group: IGroupEntry;
    navState: INavState;
    handleUserSelect: (userId: string) => void;
    groupItems: JSX.Element[];
    searchActive: boolean;
};

const GroupUserList: React.FC<IProps> = ({ group, navState, handleUserSelect, groupItems, searchActive }: IProps) => {
    const { ui: uiState, setCurrentUser } = useContext(MainContext);
    const queryClient = useQueryClient();
    const [addresses, setAddresses] = useState<IAddressDetailsPaged[]>([]);
    const [addressTags, setAddressTags] = useState<ITag[]>([]);
    const [licenses, setLicenses] = useState<ILicenseDetailsPaged[]>([]);
    const [licenseTags, setLicenseTags] = useState<ITag[]>([]);
    const [credits, setCredits] = useState<ICreditDetailsPaged[]>([]);
    const [creditTags, setCreditTags] = useState<ITag[]>([]);
    const [statusTags, setStatusTags] = useState<ITagMinimal[]>([]);
    const [roles, setRoles] = useState<IRoleAssignmentPaged[]>([]);
    const [roleTags, setRoleTags] = useState<ITag[]>([]);
    const [membersQueryFetching, setMembersQueryFetching] = useState<boolean>(false);
    const [addressTagsLoading, setAddressTagsLoading] = useState<string[]>([]);
    const [licenseTagsLoading, setLicenseTagsLoading] = useState<string[]>([]);
    const [creditTagsLoading, setCreditTagsLoading] = useState<string[]>([]);
    const [roleTagsLoading, setRoleTagsLoading] = useState<string[]>([]);

    const groupId = group.groupId;
    const isRootGroup = group.parentGroupId === getSystemId();
    const canRemoveBase = findLowestContext("UserGroup/RemoveMembers") === SystemContext;

    /* ---------- Queries & Mutations ------------- */

    /**
     * Get group members.
     */
    const membersQuery = useInfiniteQuery(
        [QueryKeys.GroupMembers, groupId],
        async ({ pageParam = 0 }) => {
            if (pageParam === 0) {
                setMembersQueryFetching(true);
            }
            const service = new UserGroupService();
            const isAdmin = checkCapWithContext("Account/Create", GroupContext, groupId);
            const result = await service.getMembers([groupId], pageParam, isAdmin);
            if (pageParam === 0) {
                setMembersQueryFetching(false);
            }
            return !result.hasErrors ? result.value : null;
        },
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries([QueryKeys.GroupUserRoles, groupId]);
                await queryClient.invalidateQueries([QueryKeys.GroupUserAddresses, groupId]);
                await queryClient.invalidateQueries([QueryKeys.GroupUserCredits, groupId]);
                await queryClient.invalidateQueries([QueryKeys.GroupUserLicenses, groupId]);
            },
            enabled:
                !!SessionManager.getServiceUser()?.userId &&
                (navState.currentLevel === group?.level - 1 || uiState.currentGroup?.instance === groupId),
            staleTime: Infinity,
            refetchOnWindowFocus: true,
            initialData: { pages: [], pageParams: [0] },
            getNextPageParam: (payload) => (payload?.nextPage === -1 ? undefined : payload?.nextPage),
        }
    );

    /**
     * Remove group members.
     */
    const groupRemoveMembers = useMutation(
        async (userIds: string[]) => {
            const service = new UserGroupService();
            return await service.removeMembers(userIds, groupId);
        },
        {
            onSuccess: async (res) => {
                if (!res?.errors?.length) {
                    await queryClient.invalidateQueries([QueryKeys.UserGroups]);
                    await queryClient.invalidateQueries([QueryKeys.GroupMembers, groupId]);
                    await queryClient.invalidateQueries([QueryKeys.UserMemberships]);
                    notification.success({ message: "Removed from group." });
                    setCurrentUser(null);
                } else {
                    res.errors.forEach((message) => {
                        notification.error({ message });
                    });
                }
            },
        }
    );

    /**
     * Get user roles.
     */
    useQueries({
        queries: membersQuery.data.pages.map((page, index) => {
            const service = new UserService();
            return {
                queryKey: [QueryKeys.GroupUserRoles, groupId, index],
                queryFn: async () => {
                    const users = page.users.map((x) => x.userId);
                    if (users.length === 0) return [];
                    const fetchedRoles = await service.getRoles(users);
                    if (!fetchedRoles.hasErrors) {
                        return fetchedRoles.value;
                    }
                    return [];
                },
                onSuccess: async (data) => {
                    const newRoles: IRoleAssignmentPaged[] = data.map((x) => {
                        return { ...x, page: index };
                    });
                    if (newRoles.length === 0) return;
                    const current = [...roles, ...newRoles];
                    setRoles(current);
                },
                enabled:
                    !!uiState.currentGroup &&
                    uiState.currentGroup?.instance === groupId &&
                    !searchActive &&
                    page?.users?.length > 0,
                refetchOnWindowFocus: false,
                staleTime: Infinity,
            };
        }),
    });

    /**
     * Create role assignment.
     */
    const roleAddUser = useMutation(
        async (role: IRoleAssignmentPaged) => {
            setRoleTagsLoading([...roleTagsLoading, role.userId]);
            const service = new UserService();
            await service.assignRoles([role.userId], role.roleId, groupId);
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Role assigned." });
                await queryClient.invalidateQueries([QueryKeys.GroupUserRoles, groupId]);
                await queryClient.invalidateQueries([QueryKeys.UserMemberships]);
            },
        }
    );

    /**
     * Remove role assignment.
     */
    const roleRemoveUser = useMutation(
        async (role: IRoleAssignmentPaged) => {
            setRoleTagsLoading([...roleTagsLoading, role.userId]);
            const service = new UserService();
            await service.removeRoles([role.userId], role.roleId, groupId);
        },
        {
            onSuccess: async (data, variables: IRoleAssignmentPaged) => {
                notification.success({ message: "Role removed." });
                await queryClient.invalidateQueries([QueryKeys.GroupUserRoles, groupId]);
                await queryClient.invalidateQueries([QueryKeys.UserMemberships]);
                const updatedRoles = roles.filter((role) => role.roleId !== variables.roleId);
                setRoles(updatedRoles);
            },
        }
    );

    /**
     * Get user addresses.
     */
    useQueries({
        queries: membersQuery.data.pages.map((page, index) => {
            const service = new AddressService();
            return {
                queryKey: [QueryKeys.GroupUserAddresses, groupId, index],
                queryFn: async () => {
                    const users = page.users?.map((x) => x.userId);
                    if (users.length === 0) return [];
                    const fetchedAddresses = await service.getUser(users);
                    if (!fetchedAddresses.hasErrors) {
                        return fetchedAddresses.value;
                    }
                    return [];
                },
                onSuccess: async (data) => {
                    const newAddresses: IAddressDetailsPaged[] = data.map((x) => {
                        return { ...x, page: index };
                    });
                    if (newAddresses.length === 0) return;
                    const current = [...addresses, ...newAddresses];
                    setAddresses(current);
                },
                enabled:
                    !!uiState.currentGroup &&
                    uiState.currentGroup?.instance === groupId &&
                    !searchActive &&
                    !membersQueryFetching &&
                    page?.users?.length > 0,
                refetchOnWindowFocus: false,
                staleTime: Infinity,
            };
        }),
    });

    /**
     * Create user address.
     */
    const addressCreateUser = useMutation(
        async (addressDetails: IAddressDetailsPaged) => {
            setAddressTagsLoading([...addressTagsLoading, addressDetails.instanceId]);
            const service = new AddressService();
            const newAddress = await service.createUser(addressDetails, addressDetails.instanceId);
            if (!newAddress.hasErrors) {
                setCurrentUser({ instance: addressDetails.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Address created." });
                await queryClient.invalidateQueries([QueryKeys.GroupUserAddresses, groupId]);
            },
        }
    );

    /**
     * Delete user address.
     */
    const addressDeleteUser = useMutation(
        async (addressDetails: IAddressDetailsPaged) => {
            setAddressTagsLoading([...addressTagsLoading, addressDetails.instanceId]);
            const service = new AddressService();
            const deleteAddress = await service.delete(addressDetails.addressId);
            if (!deleteAddress.hasErrors) {
                setCurrentUser({ instance: addressDetails.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "Address deleted." });
                await queryClient.invalidateQueries([QueryKeys.GroupUserAddresses, groupId]);
            },
        }
    );

    /**
     *  Get user licenses.
     */
    useQueries({
        queries: membersQuery.data.pages.map((page, index) => {
            const service = new LicenseService();
            return {
                queryKey: [QueryKeys.GroupUserLicenses, groupId, index],
                queryFn: async () => {
                    const users = page.users?.map((x) => x.userId);
                    if (users.length === 0) return [];
                    const fetchedLicenses = await service.getUser(users);
                    if (!fetchedLicenses.hasErrors) {
                        return fetchedLicenses.value;
                    }
                    return [];
                },
                onSuccess: async (data) => {
                    const newLicenses: ILicenseDetailsPaged[] = data.map((x) => {
                        return { ...x, page: index };
                    });
                    if (newLicenses.length === 0) return;
                    const current = [...licenses, ...newLicenses];
                    setLicenses(current);
                },
                enabled:
                    !!uiState.currentGroup &&
                    uiState.currentGroup?.instance === groupId &&
                    !searchActive &&
                    !membersQueryFetching &&
                    page?.users?.length > 0,
                refetchOnWindowFocus: false,
                staleTime: Infinity,
            };
        }),
    });

    /**
     * Create user license.
     */
    const licenseCreate = useMutation(
        async (licenseDetails: ILicenseCreationDetails) => {
            notification.info({ message: "Creating license." });
            setLicenseTagsLoading([...licenseTagsLoading, licenseDetails.instanceId]);
            const service = new LicenseService();
            const newLicense = await service.create(licenseDetails);
            if (!newLicense.hasErrors) {
                setCurrentUser({ instance: licenseDetails.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "User license created." });
                await queryClient.invalidateQueries([QueryKeys.GroupUserLicenses, groupId]);
            },
        }
    );

    /**
     * User credit queries.
     */
    useQueries({
        queries: membersQuery.data.pages.map((page, index) => {
            const service = new CreditService();
            return {
                queryKey: [QueryKeys.GroupUserCredits, groupId, index],
                queryFn: async () => {
                    const users = page.users?.map((x) => x.userId);
                    if (users.length === 0) return [];
                    const fetchedCredits = await service.getUser(users);
                    if (!fetchedCredits.hasErrors) {
                        return fetchedCredits.value;
                    }
                    return [];
                },
                onSuccess: async (data) => {
                    const newCredits: ICreditDetailsPaged[] = data.map((x) => {
                        return { ...x, page: index };
                    });
                    if (newCredits.length === 0) return;
                    const current = [...credits, ...newCredits];
                    setCredits(current);
                },
                enabled:
                    !!uiState.currentGroup &&
                    uiState.currentGroup?.instance === groupId &&
                    !searchActive &&
                    !membersQueryFetching &&
                    page?.users?.length > 0,
                refetchOnWindowFocus: false,
                staleTime: Infinity,
            };
        }),
    });

    /**
     * Create user credit mutation
     */
    const creditCreateUser = useMutation(
        async (creditDetails: ICreditDetailsPaged) => {
            setCreditTagsLoading([...creditTagsLoading, creditDetails.instanceId]);
            const service = new CreditService();
            const newCredit = await service.createUser(creditDetails, creditDetails.instanceId);
            if (!newCredit.hasErrors) {
                setCurrentUser({ instance: creditDetails.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "User credit account created." });
                await queryClient.invalidateQueries([QueryKeys.GroupUserCredits, groupId]);
                await queryClient.invalidateQueries([QueryKeys.GroupUserAddresses, groupId]);
            },
        }
    );

    /**
     * Delete user credit mutation
     */
    const creditDeleteUser = useMutation(
        async (creditDetails: ICreditDetailsPaged) => {
            setCreditTagsLoading([...creditTagsLoading, creditDetails.instanceId]);
            const service = new CreditService();
            const deleteCredit = await service.delete([creditDetails.creditId]);
            if (!deleteCredit.hasErrors) {
                setCurrentUser({ instance: creditDetails.instanceId });
            }
        },
        {
            onSuccess: async () => {
                notification.success({ message: "User credit account deleted." });
                await queryClient.invalidateQueries([QueryKeys.GroupUserCredits, groupId]);
            },
        }
    );

    /**
     * Add member to subgroup. Called via the assignment menu.
     */
    const groupAddUser = useMutation(async (assignment: IGroupAssignmentDetails) => {
        const service = new UserGroupService();
        const res = await service.addMembers(assignment.groupId, assignment.userIds);
        if (!res.hasErrors) {
            notification.success({ message: "Added to group." });
            await queryClient.invalidateQueries([QueryKeys.UserGroups]);
            await queryClient.invalidateQueries([GroupsQueryKeys.GroupUsers]);
            await queryClient.invalidateQueries([QueryKeys.GroupMembers, assignment.groupId]);
            await queryClient.invalidateQueries([QueryKeys.GroupUserRoles, assignment.groupId]);
            await queryClient.invalidateQueries([QueryKeys.UserMemberships]);
        } else {
            notification.warning({ message: "Already added." });
        }
    });

    /* ---------- Handlers ------------- */

    /**
     * Perform mutations based upon which option is selected from the user assignment menu
     * @param type
     * @param params
     */
    const addAction = (type: TagType, params: any) => {
        switch (type) {
            case TagType.AddressUser:
                addressCreateUser.mutate(params);
                break;
            case TagType.LicenseUser:
                licenseCreate.mutate(params);
                break;
            case TagType.CreditUser:
                creditCreateUser.mutate(params);
                break;
            case TagType.Role:
                roleAddUser.mutate(params);
                break;
            case TagType.Assignment:
                groupAddUser.mutate(params);
                break;
        }
    };

    /**
     * Remove a user from a group
     * @param userId
     */
    const removeAction = (userId: string) => {
        groupRemoveMembers.mutate([userId]);
    };

    /**
     * Rebuild address tags on demand. Check for credit tags to enable delete
     */
    useEffect(() => {
        const tags = { ...addressTags };
        if (addresses?.length === 0 && !!tags && Object.keys(tags)?.length === 0) return;
        addresses.forEach((address) => {
            let enableDelete = true;
            if (credits.length > 0) {
                if (
                    credits?.findIndex((x) => x.contextId === address.contextId && x.instanceId === address.instanceId)
                ) {
                    enableDelete = false;
                }
            }
            tags[address.addressId] = getAddressUserTag(groupId, address, enableDelete, () =>
                addressDeleteUser.mutate(address)
            );
        });
        setAddressTags(tags);
        setAddressTagsLoading([]);
    }, [addresses]);

    /**
     * Rebuild license tags on demand
     */
    useEffect(() => {
        const tags = { ...licenseTags };
        if (licenses?.length === 0 && !!tags && Object.keys(tags)?.length === 0) return;
        licenses.forEach((license) => {
            tags[license.licenseId] = getLicenseUserTag(
                groupId,
                license,
                null // Licenses cannot be deleted via their tag
            );
        });
        setLicenseTags(tags);
        setLicenseTagsLoading([]);
    }, [licenses]);

    /**
     * Rebuild credit tags on demand
     */
    useEffect(() => {
        const tags = { ...creditTags };
        if (credits?.length === 0 && !!tags && Object.keys(tags)?.length === 0) return;
        credits.forEach((credit) => {
            tags[credit.creditId] = getCreditUserTag(groupId, credit, () => creditDeleteUser.mutate(credit));
        });

        setCreditTags(tags);
        setCreditTagsLoading([]);
    }, [credits]);

    /**
     * Rebuild role tags on demand
     */
    useEffect(() => {
        const tags = { ...roleTags };
        if (roles?.length === 0 && !!tags && Object.keys(tags)?.length === 0) return;
        roles.forEach((role) => {
            if (
                role.contextId === SystemContext ||
                role.contextId === UserContext ||
                (role.contextId === GroupContext && role.instanceIds.map((x) => x.id).includes(groupId))
            ) {
                tags[role.roleAssignmentId] = getRoleUserTag(role.instanceIds?.map((x) => x.id)[0], role, () =>
                    roleRemoveUser.mutate(role)
                );
            }
        });
        setRoleTags(tags);
        setRoleTagsLoading([]);
    }, [roles]);

    /**
     * Get statuses from users
     */
    useEffect(() => {
        const tags: ITagMinimal[] = [];
        membersQuery?.data?.pages?.forEach((page, index) =>
            page?.users?.map(({ userId, status }) => {
                if (status !== "invited" && status !== "suspended" && status !== "deactivated") return null;
                return tags.push({
                    userId,
                    label: prettifyName(status),
                    page: index,
                    type: TagType.Status,
                    groupId,
                });
            })
        );
        setStatusTags(tags);
    }, [groupId, membersQuery?.data]);

    useEffect(() => {
        membersQuery.refetch().then();
    }, []);

    const pages = membersQuery.data?.pages;

    // Flatten the user payload array down to a single list of user models, with page indices
    let users: IUserDetailsPaged[] = [];
    pages.forEach((page, index) => {
        users = users
            .concat(
                page?.users.map((user) => ({
                    ...user,
                    page: index,
                    isActive: uiState?.currentUser?.instance === user.userId,
                }))
            )
            .sort((a, b) => a?.fullName?.localeCompare(b?.fullName));
    });

    return membersQueryFetching ? (
        <List itemLayout="vertical" dataSource={[...Array(6).keys()]} renderItem={() => <SkeletonRows />} />
    ) : (
        <InfiniteUserList
            users={users}
            groupItems={groupItems}
            hasNextPage={membersQuery.hasNextPage}
            isNextPageLoading={membersQuery.isLoading}
            loadNextPage={membersQuery.fetchNextPage}
            groupId={groupId}
            handleUserSelect={handleUserSelect}
            removeAction={!isRootGroup || canRemoveBase ? removeAction : null}
            addAction={addAction}
            roleTags={Object.values(roleTags)}
            addressTags={Object.values(addressTags)}
            licenseTags={Object.values(licenseTags)}
            creditTags={Object.values(creditTags)}
            statusTags={statusTags}
            addressAssignments={addresses}
            creditAssignments={credits}
            roleAssignments={roles}
            addressTagsLoading={addressTagsLoading}
            licenseTagsLoading={licenseTagsLoading}
            creditTagsLoading={creditTagsLoading}
            roleTagsLoading={roleTagsLoading}
        />
    );
};
export default GroupUserList;
